import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../../../services/axiosInterceptors";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const KIWI_COMPANY_ID = "6a6b4ce723abe63bde91abf2";

const EXTENSIONS = new Set(['.wav', '.mp3', '.mpeg', '.m4a', '.mp4', '.aac', '.flac', '.ogg', '.wma', '.aiff', '.alac', '.opus']);
const LANGUAGES = [
  ['en-IN', 'English (Indian)'],
  ['hi-IN', 'Hindi'],
  ['gu-IN', 'Gujarati'],
  ['bn-IN', 'Bengali'],
  ['mr-IN', 'Marathi'],
  ['ta-IN', 'Tamil'],
  ['te-IN', 'Telugu'],
  ['kn-IN', 'Kannada']
];

export default function KiwiRecordingsPanel({ onRefresh }) {
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : null;
  const [activeTab, setActiveTab] = useState("sync");
  const [files, setFiles] = useState([]);
  const [language, setLanguage] = useState("en-IN");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busyUpload, setBusyUpload] = useState(false);
  const [busyMode, setBusyMode] = useState("");
  const [message, setMessage] = useState({ type: "info", text: "" });
  const inputRef = useRef(null);

  const isKiwi = decodedToken?.companyId === KIWI_COMPANY_ID;

  if (!isKiwi) return null;

  const runSync = async () => {
    setBusyMode("syncing");
    try {
      const res = await axiosInstance.post(
        apiEndPointsConfig.kiwiElevenLabsSync,
        { importAll: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const results = res?.data?.data?.syncData?.results || res?.data?.data?.results || [];
      const failed = results.filter((item) => item.status === "failed").length;
      setMessage({
        type: failed ? "warning" : "success",
        text: `Kiwi conversation summary sync triggered: ${results.filter((item) => item.status === "synced").length} synced, ${failed} failed.`,
      });
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || "Failed to sync Kiwi recordings." });
    } finally {
      setBusyMode("");
    }
  };

  const chooseFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    const valid = selected.filter((file) => EXTENSIONS.has(file.name.slice(file.name.lastIndexOf('.')).toLowerCase()));
    if (valid.length !== selected.length) {
      setMessage({ type: 'warning', text: 'Unsupported files were removed. Please select audio files only.' });
    }
    setFiles(valid);
  };

  const submitManualUpload = async (event) => {
    event.preventDefault();
    if (!files.length) {
      setMessage({ type: 'error', text: 'Select at least one audio file.' });
      return;
    }
    setBusyUpload(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('language', language);
      formData.append('date', date);
      await axiosInstance.post(apiEndPointsConfig.upload, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      setMessage({ type: 'success', text: 'Files uploaded successfully. Analysis will appear when processing completes.' });
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.status?.value || error.response?.data?.message || 'Upload failed.'
      });
    } finally {
      setBusyUpload(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Box sx={{ pb: 0 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Kiwi Recordings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kiwi conversations, audio, details, and analytics are handled by isolated crons. Use the actions below to trigger a summary sync on demand or upload a file manually.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(event, value) => {
          setActiveTab(value);
          setMessage({ type: "info", text: "" });
        }}
        sx={{ borderBottom: '1px solid #eee', mb: 3 }}
      >
        <Tab value="sync" label="Sync Conversations" sx={{ textTransform: "none", fontWeight: 600 }} />
        <Tab value="manual" label="Manual Upload" sx={{ textTransform: "none", fontWeight: 600 }} />
      </Tabs>

      {!!message.text && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      {activeTab === "sync" ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={runSync}
            disabled={Boolean(busyMode)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {busyMode === "syncing" ? <CircularProgress size={18} sx={{ color: "white", mr: 1 }} /> : null}
            Sync Kiwi Conversations
          </Button>
          <Button
            variant="text"
            onClick={() => typeof onRefresh === "function" && onRefresh()}
            disabled={Boolean(busyMode)}
            sx={{ textTransform: "none" }}
          >
            Refresh Table
          </Button>
        </Stack>
      ) : (
        <Box component="form" onSubmit={submitManualUpload} sx={{ mb: 3 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, alignItems: 'center' }}>
            <Button variant="outlined" component="label" sx={{ height: 56, textTransform: 'none', borderRadius: 2 }}>
              {files.length ? `${files.length} file(s) selected` : 'Choose audio files'}
              <input ref={inputRef} hidden type="file" multiple onChange={chooseFiles} />
            </Button>
            <FormControl fullWidth>
              <InputLabel>Audio language</InputLabel>
              <Select value={language} label="Audio language" onChange={(event) => setLanguage(event.target.value)}>
                {LANGUAGES.map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="date"
              label="Recording date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={busyUpload || !files.length}
            sx={{ mt: 2, textTransform: 'none', borderRadius: 2 }}
          >
            {busyUpload ? <><CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />Uploading...</> : 'Upload and Analyze'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
